import React from 'react';
import PropTypes from 'prop-types';
import { FieldArray, useFormikContext } from 'formik';
import { get } from 'lodash';

import AlertBanner from '~/components/core/AlertBanner';
import Button from '~/components/core/Atomic/Buttons/Button';
import DndOrderedList from '~/components/core/DndOrderedList/DndOrderedList';
import { AddIcon } from '~/components/deprecatedMuiIcons';

import copies from '../../copies.json';
import { markNewRow } from '../../newRowIdentity';
import { reorderArrayField } from '../../reorderArrayField';
import BaseConfigurationSection from '../BaseConfigurationSection';

import CategoryCard from './CategoryCard';

const ID_PREFIX = 'configuration.categories';

const CategoriesSection = () => {
  const { values } = useFormikContext();

  const categories = get(values, ID_PREFIX, []);

  const getNewField = ({ index }) =>
    markNewRow({
      id: `category_${index}`,
      desc: `Category ${index}`,
    });

  return (
    <BaseConfigurationSection title="Edit Categories List">
      <div className="mt-16">
        <AlertBanner alertType={AlertBanner.ALERT_TYPES.INFO} note={copies.categories.categories_note} withIcon />
        <div className="pb-20" />
        <FieldArray
          name={ID_PREFIX}
          render={(arrayHelpers) => (
            <div className="mr-6 flex flex-col gap-12">
              <DndOrderedList
                className="flex flex-col gap-12"
                items={(Array.isArray(categories) ? categories : []).map((category, idx) => ({
                  idx,
                  // Key on the position, not the category's `id`. Category ids are user-editable
                  // (Display Name syncs to Key via snakeCase), so an id-derived key changes mid-edit
                  // and remounts the row, dropping input focus. The index is stable while typing.
                  dndId: String(idx),
                }))}
                getItemId={(item) => item.dndId}
                onReorder={({ fromIndex, toIndex }) => reorderArrayField(arrayHelpers, fromIndex, toIndex)}
                renderItem={({ idx }, { dragHandleProps }) => (
                  <CategoryCard index={idx} arrayHelpers={arrayHelpers} dragHandleProps={dragHandleProps} />
                )}
              />
              <div className="flex">
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => arrayHelpers.push(getNewField({ index: categories?.length + 1 }))}
                >
                  <AddIcon />
                  ADD A CATEGORY
                </Button>
              </div>
            </div>
          )}
        />
      </div>
    </BaseConfigurationSection>
  );
};

CategoriesSection.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default CategoriesSection;
